import { useState, useEffect, useMemo } from "react";
import { runQuery } from "../services/api";

const SQL = `
SELECT TIP, KOD, ISIM, YIL, AY, GUN, SUM(TUTAR) AS TUTAR
FROM EMW_NAKITAKIS
GROUP BY TIP, KOD, ISIM, YIL, AY, GUN
ORDER BY TIP, KOD, YIL, AY, GUN
`.trim();

const AYLAR_KISA = ["", "Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const TUM_AYLAR = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const GRANULARITELER = [
  { id: "yil", label: "Yıl" },
  { id: "ay", label: "Yıl + Ay" },
  { id: "gun", label: "Yıl + Ay + Gün" },
];

const ONCEKI_KEY = "__onceki__";

function periodKey(r, gran) {
  if (gran === "yil") return `${r.YIL}`;
  if (gran === "ay") return `${r.YIL}-${String(r.AY).padStart(2, "0")}`;
  return `${r.YIL}-${String(r.AY).padStart(2, "0")}-${String(r.GUN).padStart(2, "0")}`;
}

function periodLabel(key, gran) {
  if (key === ONCEKI_KEY) return "Önceki";
  if (gran === "yil") return key;
  if (gran === "ay") {
    const [y, m] = key.split("-");
    return `${AYLAR_KISA[Number(m)] || m} ${y}`;
  }
  const [y, m, d] = key.split("-");
  return `${d}.${m}.${y}`;
}

function fmtTutar(v) {
  if (v === undefined || v === null) return "—";
  const n = Number(v);
  const s = Math.abs(n).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (n < 0 ? "-" : "") + s;
}

const inp = { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 7, padding: "8px 11px", color: "var(--text-primary)", fontSize: 13, outline: "none", boxSizing: "border-box" };
const chip = (active) => ({
  padding: "6px 13px", borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: "pointer",
  border: `1px solid ${active ? "var(--accent)" : "var(--border)"}`,
  background: active ? "var(--accent-bg)" : "var(--bg-card)",
  color: active ? "var(--accent-text)" : "var(--text-secondary)",
  whiteSpace: "nowrap",
});
const stickyCol = (bg) => ({ position: "sticky", left: 0, background: bg, zIndex: 1 });
const stickyCol2 = (bg, offset) => ({ position: "sticky", left: offset, background: bg, zIndex: 1 });

export default function NakitAkisReport() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [granularite, setGranularite] = useState("yil");
  const [search, setSearch] = useState("");
  const [selectedTips, setSelectedTips] = useState(null); // null => henüz başlatılmadı
  const [baslangicYil, setBaslangicYil] = useState(null); // bu yıl/aydan öncesi "Önceki"'ye toplanır
  const [baslangicAy, setBaslangicAy] = useState(null);
  const [collapsedTips, setCollapsedTips] = useState(() => new Set());

  function fetchData() {
    setLoading(true);
    setError(null);
    runQuery(SQL)
      .then((r) => setRows(r.data.rows))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchData(); }, []);

  const allTips = useMemo(() => (rows ? [...new Set(rows.map((r) => r.TIP))].sort((a, b) => a.localeCompare(b, "tr", { numeric: true })) : []), [rows]);
  const allYears = useMemo(() => (rows ? [...new Set(rows.map((r) => r.YIL))].sort((a, b) => a - b) : []), [rows]);

  useEffect(() => {
    if (rows && selectedTips === null) {
      // Varsayılan: Müşteri Siparişleri hariç tüm tipler seçili
      setSelectedTips(new Set(allTips.filter((t) => !t.startsWith("109-"))));
    }
  }, [rows]);
  useEffect(() => {
    if (rows && baslangicYil === null) {
      const buYil = new Date().getFullYear();
      setBaslangicYil(allYears.includes(buYil) ? buYil : (allYears[0] ?? buYil));
    }
  }, [rows]);
  useEffect(() => {
    if (rows && baslangicAy === null) setBaslangicAy(new Date().getMonth() + 1);
  }, [rows]);

  // Tip/arama filtresine uyan tüm satırlar (yıl/ay eşiği henüz uygulanmadı).
  const baseFilteredRows = useMemo(() => {
    if (!rows || !selectedTips) return [];
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (!selectedTips.has(r.TIP)) return false;
      if (q && !(String(r.KOD || "").toLowerCase().includes(q) || String(r.ISIM || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [rows, selectedTips, search]);

  const { tipGruplari, columns, genelToplam, toplamHareket } = useMemo(() => {
    if (baslangicYil === null || baslangicAy === null || baseFilteredRows.length === 0) {
      return { tipGruplari: [], columns: [], genelToplam: { cells: {}, toplam: 0 }, toplamHareket: 0 };
    }

    const colSet = new Set();
    const tipMap = new Map();
    const tipToplam = new Map();
    const grand = { cells: {}, toplam: 0 };
    let oncekiVarMi = false;

    for (const r of baseFilteredRows) {
      const secili = r.YIL > baslangicYil || (r.YIL === baslangicYil && r.AY >= baslangicAy);
      const pk = secili ? periodKey(r, granularite) : ONCEKI_KEY;
      if (!secili) oncekiVarMi = true;

      colSet.add(pk);
      const tutar = Number(r.TUTAR) || 0;

      if (!tipMap.has(r.TIP)) tipMap.set(r.TIP, new Map());
      const rowKey = `${r.KOD}||${r.ISIM}`;
      const rowsMap = tipMap.get(r.TIP);
      if (!rowsMap.has(rowKey)) rowsMap.set(rowKey, { kod: r.KOD, isim: r.ISIM, cells: {}, toplam: 0 });
      const rowObj = rowsMap.get(rowKey);
      rowObj.cells[pk] = (rowObj.cells[pk] || 0) + tutar;
      rowObj.toplam += tutar;

      if (!tipToplam.has(r.TIP)) tipToplam.set(r.TIP, { cells: {}, toplam: 0 });
      const tObj = tipToplam.get(r.TIP);
      tObj.cells[pk] = (tObj.cells[pk] || 0) + tutar;
      tObj.toplam += tutar;

      grand.cells[pk] = (grand.cells[pk] || 0) + tutar;
      grand.toplam += tutar;
    }

    const columns = [...colSet].filter((c) => c !== ONCEKI_KEY).sort();
    if (oncekiVarMi) columns.unshift(ONCEKI_KEY);

    const tipGruplari = [...tipMap.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], "tr", { numeric: true }))
      .map(([tip, rowsMap]) => ({
        tip,
        rows: [...rowsMap.values()].sort((a, b) => String(a.kod || "").localeCompare(String(b.kod || ""), "tr", { numeric: true })),
        toplam: tipToplam.get(tip),
      }));

    return { tipGruplari, columns, genelToplam: grand, toplamHareket: baseFilteredRows.length };
  }, [baseFilteredRows, granularite, baslangicYil, baslangicAy]);

  function toggleTip(tip) {
    setSelectedTips((prev) => {
      const next = new Set(prev);
      if (next.has(tip)) next.delete(tip); else next.add(tip);
      return next;
    });
  }
  function toggleCollapse(tip) {
    setCollapsedTips((prev) => {
      const next = new Set(prev);
      if (next.has(tip)) next.delete(tip); else next.add(tip);
      return next;
    });
  }

  const kodSayisi = tipGruplari.reduce((s, g) => s + g.rows.length, 0);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-heading)" }}>💰 Nakit Akış</div>
          <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>
            {loading ? "Yükleniyor..." : `${kodSayisi} hesap · ${toplamHareket} hareket`}
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          style={{
            padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border)",
            background: "transparent", color: "var(--text-primary)", fontWeight: 600, fontSize: 13,
            cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1, whiteSpace: "nowrap",
          }}
        >
          {loading ? "⏳ Yükleniyor..." : "🔄 Yenile"}
        </button>
      </div>

      {error && (
        <div style={{ background: "#450a0a", border: "1px solid #7f1d1d", borderRadius: 8, padding: "12px 16px", color: "#fca5a5", fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {!loading && !error && rows && (
        <>
          {/* ── Filtreler ── */}
          <div style={{ background: "var(--bg-card-alt)", border: "1px solid var(--border)", borderRadius: 10, padding: 16, marginBottom: 18, display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Kod veya isim ara..."
                style={{ ...inp, minWidth: 220, flex: "0 1 260px" }}
              />
              <div style={{ display: "flex", gap: 6 }}>
                {GRANULARITELER.map((g) => (
                  <div key={g.id} style={chip(granularite === g.id)} onClick={() => setGranularite(g.id)}>
                    {g.label}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, marginBottom: 8, textTransform: "uppercase" }}>
                Tip {selectedTips && selectedTips.size < allTips.length && `(${selectedTips.size}/${allTips.length})`}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <div style={chip(selectedTips?.size === allTips.length)} onClick={() => setSelectedTips(new Set(allTips))}>Tümü</div>
                {allTips.map((tip) => (
                  <div key={tip} style={chip(selectedTips?.has(tip))} onClick={() => toggleTip(tip)}>{tip}</div>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, marginBottom: 8, textTransform: "uppercase" }}>
                Başlangıç Yılı
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {allYears.map((y) => (
                  <div key={y} style={chip(baslangicYil === y)} onClick={() => setBaslangicYil(y)}>{y}</div>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, marginBottom: 8, textTransform: "uppercase" }}>
                Başlangıç Ayı
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {TUM_AYLAR.map((m) => (
                  <div key={m} style={chip(baslangicAy === m)} onClick={() => setBaslangicAy(m)}>{AYLAR_KISA[m]}</div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 8 }}>
                {baslangicYil}/{String(baslangicAy).padStart(2, "0")} öncesindeki kayıtlar tabloda tek bir "Önceki" sütununda toplanır. En eski yıl + Ocak'ı seçerek tüm geçmişi tek tek görebilirsiniz.
              </div>
            </div>
          </div>

          {/* ── Pivot Tablo ── */}
          {tipGruplari.length === 0 ? (
            <div style={{ color: "var(--text-ghost)", fontSize: 14, textAlign: "center", padding: "40px 0" }}>Filtrelere uyan kayıt bulunamadı</div>
          ) : (
            <div style={{ overflow: "auto", borderRadius: 8, border: "1px solid var(--border)", maxHeight: "70vh" }}>
              <table style={{ borderCollapse: "collapse", fontSize: 12.5, width: "max-content", minWidth: "100%" }}>
                <thead>
                  <tr style={{ background: "var(--bg-card)" }}>
                    <th style={{ ...stickyCol("var(--bg-card)"), padding: "10px 14px", textAlign: "left", color: "var(--text-secondary)", fontWeight: 600, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", top: 0, zIndex: 3 }}>Kod</th>
                    <th style={{ ...stickyCol2("var(--bg-card)", 90), padding: "10px 14px", textAlign: "left", color: "var(--text-secondary)", fontWeight: 600, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", top: 0, zIndex: 3, minWidth: 260 }}>İsim</th>
                    {columns.map((c) => (
                      <th key={c} style={{ padding: "10px 14px", textAlign: "right", color: c === ONCEKI_KEY ? "var(--text-faint)" : "var(--text-secondary)", fontStyle: c === ONCEKI_KEY ? "italic" : "normal", fontWeight: 600, borderBottom: "1px solid var(--border)", borderRight: c === ONCEKI_KEY ? "1px solid var(--border)" : "none", whiteSpace: "nowrap", position: "sticky", top: 0, background: "var(--bg-card)", zIndex: 2 }}>
                        {periodLabel(c, granularite)}
                      </th>
                    ))}
                    <th style={{ padding: "10px 14px", textAlign: "right", color: "var(--accent-text)", fontWeight: 700, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", position: "sticky", top: 0, background: "var(--bg-card)", zIndex: 2 }}>Genel Toplam</th>
                  </tr>
                </thead>
                <tbody>
                  {tipGruplari.map((grup) => {
                    const collapsed = collapsedTips.has(grup.tip);
                    return (
                      <TipGroupRows
                        key={grup.tip}
                        grup={grup}
                        columns={columns}
                        collapsed={collapsed}
                        onToggle={() => toggleCollapse(grup.tip)}
                      />
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: "var(--accent-bg)" }}>
                    <td style={{ ...stickyCol("var(--accent-bg)"), padding: "10px 14px", fontWeight: 700, color: "var(--accent-text)", borderTop: "2px solid var(--accent)", whiteSpace: "nowrap" }}>Genel Toplam</td>
                    <td style={{ ...stickyCol2("var(--accent-bg)", 90), padding: "10px 14px", borderTop: "2px solid var(--accent)", minWidth: 260 }}></td>
                    {columns.map((c) => (
                      <td key={c} style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: "var(--accent-text)", fontStyle: c === ONCEKI_KEY ? "italic" : "normal", borderTop: "2px solid var(--accent)", borderRight: c === ONCEKI_KEY ? "1px solid var(--accent)" : "none", whiteSpace: "nowrap" }}>
                        {fmtTutar(genelToplam.cells[c])}
                      </td>
                    ))}
                    <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: "var(--accent-text)", borderTop: "2px solid var(--accent)", whiteSpace: "nowrap", fontSize: 13.5 }}>
                      {fmtTutar(genelToplam.toplam)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TipGroupRows({ grup, columns, collapsed, onToggle }) {
  return (
    <>
      <tr style={{ background: "var(--bg-alt-row)", cursor: "pointer" }} onClick={onToggle}>
        <td colSpan={2} style={{ ...stickyCol("var(--bg-alt-row)"), padding: "9px 14px", fontWeight: 700, color: "var(--text-heading)", borderBottom: "1px solid var(--border-hairline)", whiteSpace: "nowrap" }}>
          <span style={{ display: "inline-block", width: 14 }}>{collapsed ? "▸" : "▾"}</span> {grup.tip}
        </td>
        {columns.map((c) => (
          <td key={c} style={{ padding: "9px 14px", borderBottom: "1px solid var(--border-hairline)" }}></td>
        ))}
        <td style={{ padding: "9px 14px", borderBottom: "1px solid var(--border-hairline)" }}></td>
      </tr>

      {!collapsed && grup.rows.map((r, i) => (
        <tr
          key={`${r.kod}-${r.isim}-${i}`}
          style={{ background: i % 2 === 0 ? "var(--bg-page)" : "var(--bg-alt-row)" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--accent-bg)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? "var(--bg-page)" : "var(--bg-alt-row)")}
        >
          <td style={{ ...stickyCol(i % 2 === 0 ? "var(--bg-page)" : "var(--bg-alt-row)"), padding: "8px 14px", color: "var(--text-secondary)", borderBottom: "1px solid var(--border-hairline)", whiteSpace: "nowrap", fontFamily: "monospace" }}>{r.kod}</td>
          <td style={{ ...stickyCol2(i % 2 === 0 ? "var(--bg-page)" : "var(--bg-alt-row)", 90), padding: "8px 14px", color: "var(--text-primary)", borderBottom: "1px solid var(--border-hairline)", whiteSpace: "nowrap", minWidth: 260 }}>{r.isim}</td>
          {columns.map((c) => {
            const v = r.cells[c];
            return (
              <td key={c} style={{ padding: "8px 14px", textAlign: "right", borderBottom: "1px solid var(--border-hairline)", borderRight: c === ONCEKI_KEY ? "1px solid var(--border-hairline)" : "none", fontStyle: c === ONCEKI_KEY ? "italic" : "normal", whiteSpace: "nowrap", color: v < 0 ? "#dc2626" : v === undefined ? "var(--text-faint)" : "var(--text-primary)" }}>
                {fmtTutar(v)}
              </td>
            );
          })}
          <td style={{ padding: "8px 14px", textAlign: "right", borderBottom: "1px solid var(--border-hairline)", whiteSpace: "nowrap", fontWeight: 600, color: r.toplam < 0 ? "#dc2626" : "var(--text-heading)" }}>
            {fmtTutar(r.toplam)}
          </td>
        </tr>
      ))}

      <tr style={{ background: "var(--bg-card)" }}>
        <td colSpan={2} style={{ ...stickyCol("var(--bg-card)"), padding: "8px 14px", fontWeight: 700, color: "var(--text-heading)", borderBottom: "2px solid var(--border)", whiteSpace: "nowrap" }}>
          Toplam {grup.tip}
        </td>
        {columns.map((c) => {
          const v = grup.toplam.cells[c];
          return (
            <td key={c} style={{ padding: "8px 14px", textAlign: "right", fontWeight: 700, fontStyle: c === ONCEKI_KEY ? "italic" : "normal", borderBottom: "2px solid var(--border)", borderRight: c === ONCEKI_KEY ? "1px solid var(--border)" : "none", whiteSpace: "nowrap", color: v < 0 ? "#dc2626" : v === undefined ? "var(--text-faint)" : "var(--text-heading)" }}>
              {fmtTutar(v)}
            </td>
          );
        })}
        <td style={{ padding: "8px 14px", textAlign: "right", fontWeight: 700, borderBottom: "2px solid var(--border)", whiteSpace: "nowrap", color: grup.toplam.toplam < 0 ? "#dc2626" : "var(--text-heading)" }}>
          {fmtTutar(grup.toplam.toplam)}
        </td>
      </tr>
    </>
  );
}
